import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, CssBaseline, FormControlLabel,
  GlobalStyles, TextField, Typography,
} from '@mui/material';
import {
  acceptCookieConsent,
  getStoredThemeMode,
  getTheme,
  hasCookieConsent,
  persistThemeMode,
} from './theme';
import CookieBanner from './components/CookieBanner';
import ThemeToggle from './components/ThemeToggle';
import {
  getMishtameretToken,
  mishtameretLogin,
  setPortalSessionMode,
} from './mishtameretPortalApi';
import { getStationThemeMode, isStationPath, portalPath } from './stationMode';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStation = isStationPath(location.pathname);
  const homePath = portalPath(isStation);
  // Sync before any token read so personal/station keys do not leak across modes
  setPortalSessionMode(isStation ? 'station' : 'personal');
  const [mode, setMode] = useState(() =>
    isStation ? getStationThemeMode() : getStoredThemeMode()
  );
  const [consent, setConsent] = useState(() =>
    isStation ? true : hasCookieConsent()
  );
  const [phone, setPhone] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = getTheme(mode);

  useEffect(() => {
    let link = document.getElementById('mishtameret-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'mishtameret-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = FONT_HREF;
    document.documentElement.style.background = theme.htmlBg;
    document.body.style.background = theme.htmlBg;
  }, [theme.htmlBg]);

  if (getMishtameretToken()) {
    return <Navigate to={homePath} replace />;
  }

  const toggleTheme = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    if (!isStation && consent) persistThemeMode(next);
  };

  const handleAcceptCookies = () => {
    if (isStation) return;
    acceptCookieConsent();
    setConsent(true);
    persistThemeMode(mode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim() || !idNumber.trim()) {
      setError('נא למלא מספר טלפון ומספר זהות / קוד אישי');
      return;
    }
    setLoading(true);
    try {
      await mishtameretLogin(phone.trim(), idNumber.trim(), isStation ? false : rememberMe, {
        station: isStation,
      });
      navigate(homePath, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const serverMsg =
        (typeof err.response?.data?.error === 'string' && err.response.data.error) ||
        (typeof err.response?.data?.message === 'string' && err.response.data.message) ||
        '';
      if (serverMsg) {
        setError(serverMsg);
      } else if (status === 401) {
        setError('מספר הטלפון או מספר הזהות / הקוד האישי אינם תואמים לרישום במערכת. בדקו את הפרטים ונסו שוב.');
      } else if (status === 403) {
        setError('החשבון אינו פעיל. פנו למשרד.');
      } else if (!err.response) {
        setError('לא ניתן להתחבר לשרת. בדקו את החיבור ונסו שוב.');
      } else {
        setError('ההתחברות נכשלה. נסו שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.inputBg,
      color: theme.text,
      borderRadius: 2,
      fontFamily: theme.fontBody,
      fontSize: '1.05rem',
      minHeight: 52,
      '& fieldset': { borderColor: theme.borderSoft },
      '&:hover fieldset': { borderColor: theme.border },
      '&.Mui-focused fieldset': { borderColor: theme.accent },
    },
    '& .MuiInputLabel-root': { color: theme.textMuted, fontFamily: theme.fontBody },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.accent },
  };

  return (
    <>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: { margin: 0, fontFamily: theme.fontBody },
          html: { background: theme.htmlBg },
        }}
      />
      <Box
        sx={{
          minHeight: '100dvh',
          background: theme.pageBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
          position: 'relative',
          fontFamily: theme.fontBody,
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
          <ThemeToggle mode={mode} theme={theme} onToggle={toggleTheme} />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Box
            component="img"
            src={theme.logo}
            alt="משתמרת"
            sx={{
              width: { xs: 160, sm: 200 },
              height: 'auto',
              mb: 1,
              filter: mode === 'light' ? 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))' : 'none',
            }}
          />

          <Typography
            component="h1"
            sx={{
              fontFamily: theme.fontDisplay,
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.35rem' },
              color: theme.text,
              letterSpacing: 0,
              lineHeight: 1.25,
              textAlign: 'center',
            }}
          >
            תורתם משתמרת
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.fontBody,
              color: theme.textMuted,
              textAlign: 'center',
              mt: -1.5,
              mb: 1,
            }}
          >
            כניסה עם מספר טלפון ומספר זהות — תושבי חו״ל: קוד אישי
          </Typography>

          <TextField
            fullWidth
            label="מספר טלפון"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputProps={{ inputMode: 'tel', autoComplete: 'tel', dir: 'ltr' }}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            label="מספר זהות / קוד אישי"
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            inputProps={{ inputMode: 'numeric', autoComplete: 'off', dir: 'ltr' }}
            sx={fieldSx}
          />

          {!isStation ? (
            <FormControlLabel
              sx={{
                alignSelf: 'flex-start',
                mx: 0,
                color: theme.text,
                '& .MuiFormControlLabel-label': {
                  fontFamily: theme.fontBody,
                  fontSize: '0.95rem',
                },
              }}
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: theme.border,
                    '&.Mui-checked': { color: theme.accent },
                  }}
                />
              }
              label="זכור אותי על מחשב זה"
            />
          ) : null}

          {error ? (
            <Typography sx={{ color: theme.danger, fontFamily: theme.fontBody, width: '100%', textAlign: 'center' }}>
              {error}
            </Typography>
          ) : null}

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              mt: 1,
              minHeight: 52,
              borderRadius: 999,
              bgcolor: theme.accent,
              color: theme.accentContrast,
              fontFamily: theme.fontBody,
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: theme.shadow,
              '&:hover': { bgcolor: theme.accentSoft },
              '&.Mui-disabled': { bgcolor: theme.borderSoft, color: theme.textMuted },
            }}
          >
            {loading ? <CircularProgress size={26} sx={{ color: theme.accentContrast }} /> : 'כניסה'}
          </Button>
        </Box>

        {!isStation && !consent ? (
          <CookieBanner theme={theme} onAccept={handleAcceptCookies} />
        ) : null}
      </Box>
    </>
  );
};

export default LoginPage;
